import { ChangeEvent, SelectHTMLAttributes, useMemo, useState } from 'react'
import classNames from 'classnames'

import Container from '@/components/base/Container'
import Spinner from '@/components/base/Spinner'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { Section } from '@/types'

type FilterOption = {
  label: string
  value: number
}

type FilterProps = Partial<{
  label: string
  placeholder: string
  options: FilterOption[]
}>

export type FiltersProps = Section &
  Partial<{
    filter: FilterProps
    sort: FilterProps
    fetching: boolean
    // eslint-disable-next-line no-unused-vars
    onFilterChange: (event: ChangeEvent<HTMLSelectElement>) => void
  }>

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> &
  FilterProps & { fetching?: boolean }

const Select = ({
  label,
  options,
  placeholder,
  className,
  fetching,
  value,
  ...props
}: SelectProps) => {
  const selected = useMemo<FilterOption>(
    () => options?.find((option) => option.value.toString() === value)!,
    [value, options]
  )

  return (
    <div
      className={classNames(
        'flex basis-full items-center md:space-x-[3rem]',
        className
      )}
    >
      <label className="hidden whitespace-nowrap md:flex">
        <Typography variant="body">{label}</Typography>
      </label>
      <div className="relative w-full max-w-[24rem]">
        <button className="boder flex w-full items-center justify-between space-x-xs whitespace-nowrap rounded-xxs border border-neutral-80 bg-white p-xs text-dark">
          <span className="block">
            <Typography variant="body" className="block md:hidden">
              {selected?.label || label}
            </Typography>
            <Typography variant="body" className="hidden md:block">
              {selected?.label || placeholder}
            </Typography>
          </span>

          <SVGIcon
            symbol="select"
            className="h-[.8rem] w-[1.2rem] fill-neutral-20"
          />
        </button>
        <select
          value={value}
          className="absolute left-0 top-0 h-full w-full rounded-xxs px-xs opacity-0"
          {...props}
        >
          <option value="">{`--${placeholder}--`}</option>
          {options?.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {fetching ? <Spinner variant="medium" /> : null}
    </div>
  )
}

const Filters = ({
  filter,
  sort,
  fetching,
  onFilterChange,
  ...props
}: FiltersProps) => {
  const [currentFilter, setCurrentFilter] = useState('')
  const [currentSort, setCurrentSort] = useState('')

  return (
    <section {...props}>
      <Container>
        <div className="flex justify-between">
          {filter ? (
            <Select
              fetching={fetching}
              value={currentFilter}
              onChange={(event) => {
                setCurrentFilter(event?.target.value)
                onFilterChange && onFilterChange(event)
              }}
              className={classNames('mr-[.5rem] md:mr-0', {
                // 'justify-end': !sort
              })}
              {...filter}
            />
          ) : null}

          {sort ? (
            <Select
              value={currentSort}
              onChange={(event) => setCurrentSort(event?.target.value)}
              className="ml-[.5rem] justify-end md:ml-0"
              {...sort}
            />
          ) : null}
        </div>
      </Container>
    </section>
  )
}

export default Filters
